import { OrganizationMembershipsProfileFieldStatus } from "@/organization/people/page/__generated__/CommunityPeopleReportTableMutation.graphql"
import { MembersProfileFieldFilterOptionFragment$key } from "@/organization/people/page/filters/__generated__/MembersProfileFieldFilterOptionFragment.graphql"
import { GlobalID } from "@/relay/RelayTypes"
import { useWebFormQuestionTypes } from "@/web-form/utils/useWebFormQuestionTypes"
import { indexToLetter } from "@/web-form/utils/webFormFillerUtils"
import { MembersProfileFieldFilterState } from "@components/dropdown/profile-field/MembersProfileFieldFilter"
import { DiscoIcon, DiscoIconKinds } from "@disco-ui"
import DiscoTableFilterDropdown from "@disco-ui/table/header/search/DiscoTableFilterDropdown"
import { ArrayUtils } from "@utils/array/arrayUtils"
import { useFragment } from "react-relay"
import { graphql } from "relay-runtime"

export type ProfileFieldFilterOption = {
  value: OrganizationMembershipsProfileFieldStatus | string | null
  title: string
  fieldId: GlobalID
  fieldTitle: string
}

export interface MembersProfileFieldFilterOptionProps {
  profileFieldKey: MembersProfileFieldFilterOptionFragment$key
  filterState: MembersProfileFieldFilterState
  setFilterState: (state: MembersProfileFieldFilterState) => void
  onClose: (event: React.MouseEvent<HTMLElement>) => void
  showDefaultOptions: boolean
}

export default function MembersProfileFieldFilterOption({
  profileFieldKey,
  filterState,
  setFilterState,
  onClose,
  showDefaultOptions,
}: MembersProfileFieldFilterOptionProps) {
  const questionTypes = useWebFormQuestionTypes()
  const profileField = useFragment<MembersProfileFieldFilterOptionFragment$key>(
    graphql`
      fragment MembersProfileFieldFilterOptionFragment on ProfileField {
        id
        isDefault
        title
        webFormQuestion {
          type
          options {
            id
            label
          }
        }
      }
    `,
    profileFieldKey
  )

  return (
    <DiscoTableFilterDropdown
      testid={`MembersProfileFieldFilterOption.${profileField.title}`}
      isNested
      options={getOptions()}
      onClick={onClose}
      onOptionSelect={(option) =>
        setFilterState({
          profileFields: {
            ...filterState.profileFields,
            [profileField.id]: option,
          },
        })
      }
      selectedOption={filterState.profileFields[profileField.id]}
      dropdown={{
        icon: getIcon(),
        label: profileField.title,
      }}
    />
  )

  function getOptions() {
    const baseOptions: ProfileFieldFilterOption[] = [
      {
        fieldId: profileField.id,
        fieldTitle: profileField.title,
        value: null,
        title: "Any",
      },
      ...ArrayUtils.spreadIf(
        {
          fieldId: profileField.id,
          fieldTitle: profileField.title,
          value: "responded",
          title: "Responded",
        },
        showDefaultOptions
      ),
      ...ArrayUtils.spreadIf(
        {
          fieldId: profileField.id,
          fieldTitle: profileField.title,
          value: "not_responded",
          title: "Not Responded",
        },
        showDefaultOptions
      ),
    ]

    if (profileField.isDefault || !profileField.webFormQuestion?.options)
      return baseOptions

    const questionOptions = profileField.webFormQuestion.options.map((o, i) => ({
      fieldId: profileField.id,
      fieldTitle: profileField.title,
      value: o.id,
      title: `${indexToLetter(i)}. ${o.label}`,
    }))
    baseOptions.splice(1, 0, ...questionOptions)
    return baseOptions
  }

  function getIcon() {
    if (!profileField.isDefault) {
      return questionTypes.find((qt) => qt.value === profileField.webFormQuestion?.type)
        ?.icon as DiscoIconKinds
    }
    return (
      <DiscoIcon
        icon={profileField.title === "Local Timezone" ? "time" : "align-center"}
      />
    )
  }
}
